(() => {
  if (customElements.get('subscription-popup')) return;
  const COOKIE_NAME = 'promopopup';
  let activePopup = null;

  class SubscriptionPopup extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.shown = false;
      this.hidden = true;
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.dialog = this.querySelector('[role="dialog"]');
      if (!this.dialog) return;

      this.querySelector('.popup__close-btn')?.addEventListener('click', () => this.hide(), { signal });
      this.querySelector('.fixed-overlay')?.addEventListener('click', () => this.hide(), { signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal });
      document.addEventListener('focusin', event => {
        if (activePopup === this && !this.contains(event.target)) this.focusFirst();
      }, { signal });
      document.addEventListener('shopify:section:unload', event => {
        if (event.target === this || event.target.contains?.(this)) this.teardown();
      }, { signal });
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.schedule(), { once: true, signal });
      } else {
        this.schedule();
      }
    }

    disconnectedCallback() {
      this.teardown();
    }

    hasDismissalCookie() {
      try {
        return document.cookie.split(';').some(part => part.trim().startsWith(COOKIE_NAME + '='));
      } catch {
        return false;
      }
    }

    schedule() {
      if (!this.isConnected || !this.controller || this.hasDismissalCookie()) return;
      const mode = this.dataset.triggerOpen || 'immediately';
      const delay = Number.parseInt(this.dataset.delaySeconds, 10) || 5;
      const percent = Number.parseInt(this.dataset.scrollPercent, 10) || 25;
      this.onScroll = () => {
        const available = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        if (available > 0 && window.scrollY / available * 100 >= percent) this.show();
      };
      if (mode === 'immediately') this.show();
      if (mode === 'delay' || mode === 'delay_or_scroll') {
        this.timer = window.setTimeout(() => this.show(), delay * 1000);
      }
      if (mode === 'scroll_down' || mode === 'delay_or_scroll') {
        window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.controller.signal });
        this.onScroll();
      }
    }

    clearPending() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    focusable() {
      return [...this.dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
        .filter(element => !element.disabled && element.tabIndex >= 0 &&
          !element.closest('[hidden],[inert]') && element.getClientRects().length &&
          getComputedStyle(element).visibility !== 'hidden');
    }

    focusFirst() {
      (this.focusable()[0] || this.dialog).focus({ preventScroll: true });
    }

    lockBackground() {
      this.background = [];
      let branch = this;
      while (branch.parentElement) {
        const parent = branch.parentElement;
        for (const sibling of parent.children) {
          if (sibling === branch || sibling.matches('script,style,link')) continue;
          this.background.push([sibling, sibling.inert]);
          sibling.inert = true;
        }
        if (parent === document.body) break;
        branch = parent;
      }
    }

    show() {
      if (!this.isConnected || !this.controller || this.shown || this.hasDismissalCookie()) return;
      this.clearPending();
      let previousFocus = document.activeElement;
      if (activePopup && activePopup !== this) {
        previousFocus = activePopup.previousFocus;
        activePopup.hide({ remember: false, restoreFocus: false });
      }
      this.previousFocus = previousFocus;
      this.shown = true;
      activePopup = this;
      this.hidden = false;
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('open');
      document.body.classList.add('active-popup');
      this.lockBackground();
      this.focusFirst();
    }

    onKeydown(event) {
      if (activePopup !== this) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      } else if (event.key === 'Tab') {
        const items = this.focusable();
        const first = items[0];
        const last = items[items.length - 1];
        if (!items.length) {
          event.preventDefault();
          this.dialog.focus({ preventScroll: true });
        } else if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    hide({ remember = true, restoreFocus = true } = {}) {
      const wasOpen = activePopup === this;
      this.clearPending();
      this.classList.remove('open');
      this.hidden = true;
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      if (wasOpen) {
        activePopup = null;
        document.body.classList.remove('active-popup');
      }
      for (const [element, wasInert] of this.background || []) element.inert = wasInert;
      this.background = [];
      if (remember && wasOpen) {
        try {
          const interval = /^every_(\d+)_days$/.exec(this.dataset.repeatOpen || '');
          const days = interval ? Math.min(365, Math.max(1, Number(interval[1]))) : 1;
          document.cookie = COOKIE_NAME + '=hidden; Max-Age=' + (days * 86400) + '; Path=/; SameSite=Lax';
        } catch { /* A denied cookie must not prevent closing the dialog. */ }
      }
      if (wasOpen && restoreFocus) {
        const previous = this.previousFocus;
        const target = previous?.isConnected && !previous.closest?.('[inert],[hidden]') &&
          previous !== document.body ? previous : document.getElementById('MainContent');
        target?.focus({ preventScroll: true });
      }
      this.previousFocus = null;
    }

    teardown() {
      this.hide({ remember: false });
      this.controller?.abort();
      this.controller = null;
    }
  }

  customElements.define('subscription-popup', SubscriptionPopup);
  document.addEventListener('shopify:section:load', event => {
    const root = event.target;
    if (root.matches?.('subscription-popup')) root.connectedCallback();
    root.querySelectorAll?.('subscription-popup').forEach(element => element.connectedCallback());
  });
})();
